import { useState, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Card } from '@/components/ui'
import { ArrowLeft } from 'lucide-react'
import { useStudioStore } from '@/store'
import { formatCurrency } from '@/lib/utils'
import { getStatusColor, StatusIcon, StatusLegend } from '@/components/ui/metrics'
import { usePlanHistory } from '@/hooks/usePlanHistory'
import type { PeriodHistoryRecord } from '@/hooks/usePlanHistory'

const ALL_METRICS = [
  { key: 'marketing_budget', label: 'Бюджет маркетинга', isAmount: true, group: 'Маркетинг' },
  { key: 'cost_per_lead', label: 'Стоимость лида', isAmount: true, group: 'Маркетинг' },
  { key: 'leads', label: 'Лиды', isAmount: false, group: 'Воронка' },
  { key: 'appointments', label: 'Записи на пробное', isAmount: false, group: 'Воронка' },
  { key: 'new_visitors', label: 'Пришли на пробное', isAmount: false, group: 'Воронка' },
  { key: 'private_sessions_count', label: 'Индивидуальные (кол-во)', isAmount: false, group: 'Индивидуальные' },
  { key: 'private_sessions_amount', label: 'Индивидуальные (сумма)', isAmount: true, group: 'Индивидуальные' },
  { key: 'new_sales_count', label: 'Новые продажи (кол-во)', isAmount: false, group: 'Новые продажи' },
  { key: 'new_sales_amount', label: 'Новые продажи (сумма)', isAmount: true, group: 'Новые продажи' },
  { key: 'trial_payments', label: 'Оплаты пробного', isAmount: true, group: 'Новые продажи' },
  { key: 'renewals_count', label: 'Продления (кол-во)', isAmount: false, group: 'Продления' },
  { key: 'renewals_amount', label: 'Продления (сумма)', isAmount: true, group: 'Продления' },
  { key: 'upgrades_count', label: 'Апгрейды (кол-во)', isAmount: false, group: 'Апгрейды' },
  { key: 'upgrades_amount', label: 'Апгрейды (сумма)', isAmount: true, group: 'Апгрейды' },
  { key: 'returning_clients_count', label: 'БЧС (кол-во)', isAmount: false, group: 'Возвращающиеся' },
  { key: 'returning_clients_amount', label: 'БЧС (сумма)', isAmount: true, group: 'Возвращающиеся' },
  { key: 'membership_fees_new', label: 'Членские взносы (новые)', isAmount: true, group: 'Членские взносы' },
  { key: 'membership_fees_subscription_upgrades', label: 'Членские взносы при переходах', isAmount: true, group: 'Членские взносы' },
  { key: 'membership_fees_subscription_returning', label: 'Членские взносы БЧС', isAmount: true, group: 'Членские взносы' },
  { key: 'new_subscriptions_count', label: 'Новые подписки (кол-во)', isAmount: false, group: 'Новые подписки' },
  { key: 'new_subscriptions_amount', label: 'Новые подписки (сумма)', isAmount: true, group: 'Новые подписки' },
  { key: 'subscription_renewals_count', label: 'Продления подписок (кол-во)', isAmount: false, group: 'Продления подписок' },
  { key: 'subscription_renewals_amount', label: 'Продления подписок (сумма)', isAmount: true, group: 'Продления подписок' },
  { key: 'subscription_upgrades_count', label: 'Переходы на подписку (кол-во)', isAmount: false, group: 'Переходы на подписку' },
  { key: 'subscription_upgrades_amount', label: 'Переходы на подписку (сумма)', isAmount: true, group: 'Переходы на подписку' },
  { key: 'subscription_returning_clients_count', label: 'БЧС подписки (кол-во)', isAmount: false, group: 'Возвращающиеся (подписки)' },
  { key: 'subscription_returning_clients_amount', label: 'БЧС подписки (сумма)', isAmount: true, group: 'Возвращающиеся (подписки)' },
  { key: 'other_income', label: 'Прочие доходы', isAmount: true, group: 'Прочее' },
  { key: 'refunds_amount', label: 'Возвраты (сумма)', isAmount: true, group: 'Прочее' },
  { key: 'gross_revenue', label: 'Выручка общая', isAmount: true, group: 'Прочее' },
] as const

export function PlanHistoryPage() {
  const navigate = useNavigate()
  const { studioId } = useParams()
  const studioIntId = parseInt(studioId || '0')
  const { studios } = useStudioStore()
  const studioCurrency = useMemo(
    () => studios.find((s) => s.studio_internal_id === studioIntId)?.currency_code || 'RUB',
    [studios, studioIntId]
  )

  const [selectedMetric, setSelectedMetric] = useState<string>('gross_revenue')
  const { history, loading } = usePlanHistory(studioIntId)

  const visibleMetrics = useMemo(() => {
    return ALL_METRICS.filter((metric) =>
      history.some((period) => {
        const m = period.metrics[metric.key as keyof PeriodHistoryRecord['metrics']]
        return Math.abs(m.plan) > 0 || Math.abs(m.fact) > 0
      })
    )
  }, [history])

  // Derive the active metric key: fall back to first visible if selectedMetric is not in the list
  const activeMetricKey = visibleMetrics.some((m) => m.key === selectedMetric)
    ? selectedMetric
    : (visibleMetrics[0]?.key ?? 'gross_revenue')

  const currentMetric = visibleMetrics.find((m) => m.key === activeMetricKey)

  const fmtValue = (value: number, isAmount: boolean) =>
    isAmount ? formatCurrency(value, studioCurrency) : value.toFixed(1)

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Загрузка истории планирования...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      <div className="max-w-6xl mx-auto p-4 space-y-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-2 py-1 hover:bg-muted rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Назад</span>
          </button>
        </div>

        <Card className="p-4">
          <label className="block text-sm font-medium mb-2">Выберите показатель для отображения</label>
          <select
            value={activeMetricKey}
            onChange={(e) => setSelectedMetric(e.target.value)}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {Array.from(new Set(visibleMetrics.map((m) => m.group))).map((group) => (
              <optgroup key={group} label={group}>
                {visibleMetrics.filter((m) => m.group === group).map((metric) => (
                  <option key={metric.key} value={metric.key}>
                    {metric.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </Card>

        {history.length > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="p-4">
                <p className="text-xs text-muted-foreground mb-1">Периодов всего</p>
                <p className="text-2xl font-bold">{history.length}</p>
              </Card>
              <Card className="p-4">
                <p className="text-xs text-muted-foreground mb-1">Ср. исполнение</p>
                <p className="text-2xl font-bold text-green-600">
                  {Math.round(
                    history.reduce(
                      (acc, p) => acc + p.metrics[activeMetricKey as keyof PeriodHistoryRecord['metrics']].completion,
                      0
                    ) / history.length
                  )}%
                </p>
              </Card>
              <Card className="p-4">
                <p className="text-xs text-muted-foreground mb-1">Выполнено</p>
                <p className="text-2xl font-bold text-green-600">
                  {history.filter((p) => p.metrics[activeMetricKey as keyof PeriodHistoryRecord['metrics']].completion >= 100).length}
                </p>
              </Card>
              <Card className="p-4">
                <p className="text-xs text-muted-foreground mb-1">Ниже 80%</p>
                <p className="text-2xl font-bold text-red-600">
                  {history.filter((p) => p.metrics[activeMetricKey as keyof PeriodHistoryRecord['metrics']].completion < 80).length}
                </p>
              </Card>
            </div>

            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium">Период</th>
                      <th className="text-right p-3 text-sm font-medium">
                        План<br />
                        <span className="text-xs font-normal text-muted-foreground">(ср/нед)</span>
                      </th>
                      <th className="text-right p-3 text-sm font-medium">
                        Факт<br />
                        <span className="text-xs font-normal text-muted-foreground">(ср/нед)</span>
                      </th>
                      <th className="text-right p-3 text-sm font-medium">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((period) => {
                      const metricData = period.metrics[activeMetricKey as keyof PeriodHistoryRecord['metrics']]
                      const planWeekly = metricData.plan / period.weeks
                      const factWeekly = metricData.fact / period.weeks
                      const isAmount = currentMetric?.isAmount ?? false
                      const weeksLabel =
                        period.weeks === 1 ? 'неделя' : period.weeks === 5 ? 'недель' : 'недели'
                      return (
                        <tr key={period.period_id} className="border-b border-border hover:bg-muted/30 transition-colors">
                          <td className="p-3">
                            <p className="font-medium">{period.period_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {period.weeks} {weeksLabel}
                            </p>
                          </td>
                          <td className="p-3 text-right font-mono text-sm">
                            {fmtValue(planWeekly, isAmount)}
                          </td>
                          <td className="p-3 text-right font-mono text-sm font-semibold">
                            {fmtValue(factWeekly, isAmount)}
                          </td>
                          <td className={`p-3 text-right font-mono text-sm font-bold ${getStatusColor(metricData.completion, metricData.plan)}`}>
                            <div className="flex items-center justify-end gap-1">
                              <StatusIcon completion={metricData.completion} />
                              {Math.round(metricData.completion)}%
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        ) : (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">Нет данных по планированию для этой студии</p>
          </Card>
        )}

        <StatusLegend />
      </div>
    </div>
  )
}
